import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import { Box, ButtonBase, Typography } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { useQueryClient } from "@tanstack/react-query";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { NewBroadcastMessageModal } from "../../../components/StaffCommunication/util/Broadcast/NewBroadcastMessageModal";
import { AnimatedNumber } from "../../../components/UIToolkit/Animations/AnimatedNumber";
import { MetaChip } from "../../../components/UIToolkit/Badges/MetaChip";
import { SystemFlatSwitch } from "../../../components/UIToolkit/Clickable/SystemFlatSwitch";
import { SystemDropdown } from "../../../components/UIToolkit/Inputs/SystemDropDown/SystemDropdown";
import { StatStrip } from "../../../components/UIToolkit/molecules/StatStrip";
// Parked with the Hiring Mode segment below — restore together.
import Dialog from "../../../components/UIToolkit/Popups/Dialog";
import { useAuth } from "../../../contexts/AuthProvider";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useDepartmentFlags } from "../../../hooks/useDepartmentFlags";
import { useJobColors } from "../../../hooks/useJobColors";
import { useLocalStorageState } from "../../../hooks/useLocalStorageState";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import { useRosterLookup } from "../../../hooks/useRosterLookup";
import { ACCENT_BLUE, FONT_SIZE, GRID_RADIUS, INK } from "../../../theme/tokens";
import { EmploymentType } from "../../../utils/models";
import ActualCellNursesModal from "./components/ActualCellNursesModal";
import AddHireDialog from "./components/AddHireDialog";
import CensusModal from "./components/CensusModal";
import HiringPageSkeleton from "./components/HiringPageSkeleton";
import MoveOpportunitiesPanel from "./components/MoveOpportunitiesPanel";
import RecurringGapsTable from "./components/RecurringGapsTable";
import SuggestedHiresPanel from "./components/SuggestedHiresPanel";
import { HIRING_QUERY_KEY, useHiringPredictionData } from "./hooks/useHiringPredictionData";
import { moveRequestsStorageKey, useMoveRequests } from "./hooks/useMoveRequests";
import {
	deriveDefaultSettings,
	type HireSettings,
	hireSettingsStorageKey,
	suggestedHiresStorageKey,
	useHireSettings,
	useSuggestedHireEdits,
} from "./hooks/useSuggestedHireEdits";
import type { HiringGridCoord, HiringMetricKey, HiringRecommendation, HiringSuggestedHire } from "./types";
import { formatCoordForMessage } from "./utils/coordLabels";
import type { GapTone } from "./utils/gapTones";
import { buildMissingGrid, buildMissingToneFns, type HiringGridBundle, type MissingGrid } from "./utils/missingGrid";
import { recommendationKey, recommendationsOfType } from "./utils/moveRequests";
import {
	activeShiftFilter,
	censusOverrideStorageKey,
	DEFAULT_INCLUDE_FLAGS,
	DEFAULT_SHIFT_FILTER,
	hireShiftFilterStorageKey,
	metricFlagsStorageKey,
	moveShiftFilterStorageKey,
	resolveSelectedJobId,
	selectedJobStorageKey,
} from "./utils/preferences";
import { BLUE, SLATE } from "./utils/styleTokens";
import {
	availableShiftLengths,
	buildAppliedGrid,
	cellKey,
	DAYS_PER_WEEK,
	employmentMix,
	hireGridCoords,
	isHireVisible,
	mixSharePercent,
	type SuggestedHireDraft,
	shiftsByWeek,
	TARGET_EMPLOYMENT_MIX,
	type TemplateHours,
} from "./utils/suggestedHires";

/**
 * Which board is on screen. Stored values are stable tokens, deliberately decoupled from the tab
 * labels so re-wording a tab can't orphan everyone's stored preference.
 *
 * `hire`-type recommendations have no tab: the Suggested Hires panel answers that same gap as concrete
 * people, and a Hire card would put one gap on screen twice in two different shapes. `reduce` had a
 * board and no longer does — over-staffing is not a hiring decision, and the tab put a third answer on
 * a page that is asked two questions. `resolveTab` is what keeps a stored `"reduce"` from stranding
 * anyone who last left the page on it.
 */
const HIRING_TABS = ["hire", "move"] as const;
type HiringTab = (typeof HIRING_TABS)[number];

/** A stored tab outlives the board it names, so validate it against the tabs that exist. */
const resolveTab = (stored: HiringTab): HiringTab => (HIRING_TABS.includes(stored) ? stored : "hire");

/**
 * Which half of hiring the user is in. `prediction` is THIS page; `hub` is the already-shipped
 * candidate pipeline at `/:departmentId/hiring-chat` — a real navigation, not a view of its own here.
 */
const HIRING_TAB_STORAGE_KEY = (userId: string, departmentId: string) => `hiring-module-tab-${userId}-${departmentId}`;

// Stable empty identities, so a render with no data yet doesn't invalidate every memo below it.
const EMPTY_SUGGESTED_HIRES: HiringSuggestedHire[] = [];
const EMPTY_LABELS: string[] = [];
const EMPTY_RECOMMENDATIONS: HiringRecommendation[] = [];
const EMPTY_TEMPLATE_HOURS: TemplateHours = {};
const EMPTY_MIX = { [EmploymentType.FullTime]: 0, [EmploymentType.PartTime]: 0, [EmploymentType.PRN]: 0 } as Record<EmploymentType, number>;

/**
 * The summary strip's trailing chips (the employment mix, the requested/over-staffed counts) are
 * RECTANGULAR at the module's one chip radius — the same 6px the row chips, the legend chips, the
 * mode/tab switches and the job pill already use. `MetaChip`'s own 12px default is a full pill at
 * these heights, and a pill reads as something you can tap or dismiss; these only ever state a count.
 *
 * Passed as `sx` from here rather than added as a `StatStrip` prop: the chips are the CALLER's nodes,
 * handed to the strip through its `endSlot`, so the strip has no say in their shape and needs no new
 * prop to give them one. `MetaChip`'s default is left alone — it has callers all over the app.
 */
const SUMMARY_CHIP_SX = { borderRadius: GRID_RADIUS.CHIP } as const;

const JOB_ID_QUERY_PARAM = "job_id";

const HiringModule: FC = () => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	// `timezone` is the department's own, resolved by the provider — the grid's "active week" is a
	// question about the DEPARTMENT's today, not the viewer's.
	const { chosenDepartment } = useChosenDepartment();
	const { timezone } = useDepartmentFlags();
	const { getNurseById, isLoading: isLoadingRoster, isError: isRosterError } = useRosterLookup(chosenDepartment.department_id);
	const jobColor = useJobColors();
	const isRuntimeWeekStartEnabled = useFeatureFlag(FEATURE_FLAGS.ScheduleTemplateRuntimeWeekStart);
	const weekStartDay = isRuntimeWeekStartEnabled ? chosenDepartment.week_start_day : 0;
	const gridWeeks = chosenDepartment.master_schedule_weeks;

	const { userInfo } = useAuth();
	const departmentId = chosenDepartment.department_id;

	const queryClient = useQueryClient();
	const [searchParams, setSearchParams] = useSearchParams();
	// Persisted per user+department, and read HERE — above the fetch — because it is a query input: an
	// effect that synced it afterwards would fetch once with the wrong job and flash. `useLocalStorageState`
	// resolves its key during render, so the stored value is available on the very first one.
	const [storedJobId, setStoredJobId, clearStoredJobId] = useLocalStorageState<string | null>(selectedJobStorageKey(userInfo.id, departmentId), null);
	const urlJobId = searchParams.get(JOB_ID_QUERY_PARAM);
	const selectedJobId = resolveSelectedJobId(urlJobId, storedJobId);
	// Same reason as the job id: a query input, so it has to be readable before the first fetch. `null`
	// (never `undefined`) means "send no override" — `undefined` has no JSON form, so storing it would
	// leave a value that can't be read back.
	const [targetCensus, setTargetCensus] = useLocalStorageState<number | null>(censusOverrideStorageKey(userInfo.id, departmentId), null);
	const [hoveredCoords, setHoveredCoords] = useState<HiringGridCoord[]>([]);
	// Hovering a suggested hire highlights its cells transiently; clicking one PINS them until clicked again.
	const [hoveredHireId, setHoveredHireId] = useState<string | null>(null);
	const [pinnedHireId, setPinnedHireId] = useState<string | null>(null);
	const [selectedActualCoord, setSelectedActualCoord] = useState<HiringGridCoord | null>(null);
	const [censusModalOpen, setCensusModalOpen] = useState(false);
	const [addHireOpen, setAddHireOpen] = useState(false);
	// Reverting drops every request on the board and cannot be undone, so it asks first.
	const [revertRequestsOpen, setRevertRequestsOpen] = useState(false);
	const [broadcastNurseId, setBroadcastNurseId] = useState<number | null>(null);
	const [broadcastPrefill, setBroadcastPrefill] = useState<string>("");
	// The move request the open composer would record, held until it reports that a message was SENT.
	const [pendingRequest, setPendingRequest] = useState<{ recKey: string; nurseId: number } | null>(null);
	// Metric levers live here (not in the table) because they drive BOTH the table's "Missing" row and
	// the server-recomputed recommendations — toggling refetches with the new active set. Persisted per
	// user+department (defaults in `preferences.ts`), and read before the fetch for the same reason the
	// census override is: they are part of the query key.
	const [includeFlags, setIncludeFlags] = useLocalStorageState<Record<HiringMetricKey, boolean>>(
		metricFlagsStorageKey(userInfo.id, departmentId),
		DEFAULT_INCLUDE_FLAGS,
	);
	const includedMetrics = useMemo(() => (Object.keys(includeFlags) as HiringMetricKey[]).filter((k) => includeFlags[k]), [includeFlags]);

	const toggleMetric = (key: HiringMetricKey) =>
		setIncludeFlags((prev) => {
			const enabled = !prev[key];
			track("hiring_gaps_toggle_changed", { toggle: key, enabled });
			return { ...prev, [key]: enabled };
		});

	const [storedTab, setActiveTab] = useLocalStorageState<HiringTab>(HIRING_TAB_STORAGE_KEY(userInfo.id, departmentId), "hire");
	const activeTab = resolveTab(storedTab);

	// Read BEFORE the fetch: these two are query inputs, and `null` means "send no override" so the
	// backend derives them from the selected job instead of us guessing.
	const settingsKey = hireSettingsStorageKey(userInfo.id, departmentId);
	const { storedSettings, setStoredSettings, clearSettings } = useHireSettings(settingsKey);

	/**
	 * TWO shift filters, one per board, and they are deliberately independent stores.
	 *
	 * The boards answer different questions, so scoping them together was wrong: narrowing Hire to
	 * nights because nights is what you are recruiting for must not also hide every daytime move you
	 * could make instead of hiring. Each tab's Settings writes its own key.
	 *
	 * Both are owned here rather than inside their panels because the Recurring Gaps grid follows
	 * whichever board is open — the rows below always match the thing above them.
	 *
	 * Job-scoped: the stored values are shift LABELS from the selected job's own templates, and the same
	 * list under another job would name labels that job may not run. Keyed on `selectedJobId` — the
	 * URL's or the stored one — and NOT on `effectiveJobId`, for the same reason the job id itself is
	 * read up here: the Hire filter is a QUERY INPUT, so it has to resolve before the first fetch, and
	 * `effectiveJobId` cannot, since its fallback comes out of the response. The two differ only on a
	 * visit where no job has ever been chosen, which is also a visit with no filter stored to carry.
	 */
	const [hireShiftFilterLabels, setHireShiftFilterLabels] = useLocalStorageState<string[]>(
		hireShiftFilterStorageKey(userInfo.id, departmentId, selectedJobId ?? ""),
		DEFAULT_SHIFT_FILTER,
	);
	const [moveShiftFilterLabels, setMoveShiftFilterLabels] = useLocalStorageState<string[]>(
		moveShiftFilterStorageKey(userInfo.id, departmentId, selectedJobId ?? ""),
		DEFAULT_SHIFT_FILTER,
	);

	const { data, isLoading, isError } = useHiringPredictionData({
		// `null` is the stored form of "no override"; the query takes `undefined` for the same thing.
		census: targetCensus ?? undefined,
		jobId: selectedJobId,
		includedMetrics,
		shiftsPerWeek: storedSettings?.shiftsPerWeek,
		shiftLength: storedSettings?.shiftLength,
		shiftTypes: storedSettings?.shiftTypes,
		/**
		 * The Hire filter REGENERATES the suggestions; it does not just hide rows.
		 *
		 * Hiding was the old behaviour and it silently lost coverage: switching a shift off removed the
		 * people packed onto it and left that shift's gap unfilled, instead of re-packing those days onto
		 * the shifts still in scope. Empty = no filter, so nothing is sent and the packer sees every
		 * template.
		 *
		 * `visibleHires` below still filters what renders, because it has a second job this cannot do:
		 * stored EDITS outlive a refetch, so an edited list can still hold a row on a shift since
		 * switched off.
		 */
		hireShiftLabels: hireShiftFilterLabels.length === 0 ? undefined : hireShiftFilterLabels,
	});

	/**
	 * A stored job id outlives the job. An id the department's eligible set no longer contains strands
	 * the page on `jobUnavailable`, which has no job picker on it — recoverable when the id came from the
	 * URL (edit the address bar), permanently stuck when it came from storage. So drop the stored one
	 * instead: REMOVING the key (not writing a value back) hands the page to the payload's first eligible
	 * job on the next render. Only when the URL isn't naming a job, so a shared link still reports itself
	 * as broken rather than silently redirecting.
	 */
	useEffect(() => {
		if (!data || urlJobId !== null || storedJobId === null) {
			return;
		}
		if (!data.eligibleJobs.some((job) => job.jobId === storedJobId)) {
			clearStoredJobId();
		}
	}, [data, urlJobId, storedJobId, clearStoredJobId]);

	const effectiveJobId = selectedJobId ?? data?.eligibleJobs[0]?.jobId;
	const selectedJob = useMemo(() => data?.eligibleJobs.find((j) => j.jobId === effectiveJobId), [data?.eligibleJobs, effectiveJobId]);
	const selectedJobCatalogEntry = useMemo(() => chosenDepartment.jobs?.find((j) => j.id === effectiveJobId), [chosenDepartment.jobs, effectiveJobId]);
	const selectedJobDisplayName = selectedJobCatalogEntry?.displayName ?? "";
	/**
	 * The picker's options: one row per ELIGIBLE job, labelled from the department catalog.
	 *
	 * A label can come back empty, because the catalog does not always carry the eligible job ids (it
	 * carries none of them on some dev departments). That is a data gap and it is left visible —
	 * inventing a label here would hide it, and the id is not a name a head nurse would recognise.
	 */
	const jobDropdownItems = useMemo(() => {
		const displayNameById = new Map((chosenDepartment.jobs ?? []).map((job) => [job.id, job.displayName]));
		return (data?.eligibleJobs ?? []).map((job) => ({ value: job.jobId, label: displayNameById.get(job.jobId) ?? "", id: job.jobId }));
	}, [data?.eligibleJobs, chosenDepartment.jobs]);

	/**
	 * The pill wears the selected job's own colour — `useJobColors`, the same
	 * mapping every other job-coloured surface in the app uses — so switching job registers in the
	 * control itself, not only in its label. The caret hardcodes a neutral in its own `sx`, so it is
	 * overridden through a descendant selector (two classes) rather than by inheriting `color`, which
	 * would lose on specificity.
	 *
	 * No catalog entry means no job is selected, so the pill keeps the primitive's neutral surface.
	 *
	 * The CARET and hover border stay `INK.PRIMARY` so every job's pill has the same control affordance;
	 * the label carries the job's ink.
	 */
	const jobPillSx = useMemo(() => {
		const jobId = selectedJobCatalogEntry?.id;
		if (!jobId) {
			return { fontWeight: 600, borderRadius: GRID_RADIUS.CHIP };
		}
		const { fill, ink } = jobColor(jobId);
		return {
			fontWeight: 600,
			// Rectangular, like every other chip on this page.
			borderRadius: GRID_RADIUS.CHIP,
			backgroundColor: fill,
			color: ink,
			borderColor: "transparent",
			"& .MuiSvgIcon-root": { color: INK.PRIMARY },
			"@media (hover: hover) and (pointer: fine)": { "&:hover": { borderColor: INK.PRIMARY } },
		};
	}, [selectedJobCatalogEntry?.id, jobColor]);

	// ---- Suggested Hires -------------------------------------------------------------------------
	// Real template lengths, straight off the wire — the only trustworthy source for a row's hours pill
	// (a display label like "12AM-8AM" would otherwise have to be parsed back into a duration). Read
	// before the edit layer, which re-derives a row's pill from it when the row is re-pointed.
	const templateHours = data?.templateHours ?? EMPTY_TEMPLATE_HOURS;
	const { hires, hasEdits, renameHire, setHireShifts, setDay, setApplied, removeHire, addHire, resetHires } = useSuggestedHireEdits(
		suggestedHiresStorageKey(userInfo.id, departmentId, effectiveJobId ?? ""),
		data?.suggestedHires ?? EMPTY_SUGGESTED_HIRES,
		templateHours,
	);

	// Only a department running both 8h and 12h templates gets the SHIFT LENGTH switch.
	const shiftLengthOptions = useMemo(() => availableShiftLengths(templateHours), [templateHours]);

	/**
	 * No fallback shift length: `popularShiftHours` is REQUIRED to derive the generation defaults, so
	 * `settings` stays null until the payload has produced a selected job, and every surface that needs
	 * it is gated on that instead of rendering an invented 8-hour week.
	 *
	 * The stored length is then pinned to the one the department runs whenever there is only one. These
	 * settings are department-scoped while the templates are the JOB's, so a manager who picked `12h` on
	 * a job that has 12h shifts and then switched to an all-8h job would otherwise keep measuring every
	 * badge against a three-shift full-time week — with the control that says so hidden, because there
	 * is nothing left to choose.
	 */
	const storedOrJobSettings: HireSettings | null = storedSettings ?? (selectedJob ? deriveDefaultSettings(selectedJob.popularShiftHours) : null);
	const onlyShiftLength = shiftLengthOptions.length === 1 ? shiftLengthOptions[0] : undefined;
	const settings: HireSettings | null =
		storedOrJobSettings && onlyShiftLength !== undefined && storedOrJobSettings.shiftLength !== onlyShiftLength
			? { ...storedOrJobSettings, shiftLength: onlyShiftLength }
			: storedOrJobSettings;

	// The department's own template labels, chronological — the only shift types anyone can be hired into.
	const shiftLabels = useMemo(() => (data ? Object.keys(data.neededGrid) : EMPTY_LABELS), [data]);

	// Every label the grid can draw a row for — both chip groups list this same set, which is why the
	// filters are validated against it and not against `shiftLabels` alone.
	const allShiftLabels = useMemo(() => (data ? [...new Set([...Object.keys(data.neededGrid), ...Object.keys(data.actualGrid)])] : EMPTY_LABELS), [data]);
	const hireShiftFilter = useMemo(() => activeShiftFilter(hireShiftFilterLabels, allShiftLabels), [hireShiftFilterLabels, allShiftLabels]);
	const moveShiftFilter = useMemo(() => activeShiftFilter(moveShiftFilterLabels, allShiftLabels), [moveShiftFilterLabels, allShiftLabels]);
	/**
	 * The Hire filter, restated as the exclusion list the hire rows want. The two are inverses: the chips
	 * store what the user SELECTED (empty = no filter), a hire is hidden when every shift type it
	 * covers has been filtered out.
	 */
	const disabledShifts = useMemo(
		() => (hireShiftFilter.size === 0 ? EMPTY_LABELS : shiftLabels.filter((label) => !hireShiftFilter.has(label))),
		[hireShiftFilter, shiftLabels],
	);
	// What each chip group draws as selected: every label the grid can show, minus that board's
	// filtered-out ones. Wider than `shiftLabels` on purpose — a shift type that has coverage but no
	// need still has a row in the grid, so it still has a chip.
	const hireShiftsInScope = useMemo(
		() => (hireShiftFilter.size === 0 ? allShiftLabels : allShiftLabels.filter((label) => hireShiftFilter.has(label))),
		[allShiftLabels, hireShiftFilter],
	);
	const moveShiftsInScope = useMemo(
		() => (moveShiftFilter.size === 0 ? allShiftLabels : allShiftLabels.filter((label) => moveShiftFilter.has(label))),
		[allShiftLabels, moveShiftFilter],
	);
	// The grid sits under both boards and follows whichever is OPEN, so its rows always match the thing
	// above them. It is the one surface neither filter owns outright.
	const gridShiftFilterLabels = activeTab === "move" ? moveShiftFilterLabels : hireShiftFilterLabels;

	// A person whose every shift type the Hire filter switched off is out of scope for that BOARD —
	// hidden from its rows and out of its summary mix.
	const visibleHires = useMemo(() => hires.filter((hire) => isHireVisible(hire, disabledShifts)), [hires, disabledShifts]);
	/**
	 * Built from EVERY hire, filtered or not.
	 *
	 * Applying someone is a statement about the department ("this person exists as coverage"), not a view
	 * preference — so the overlay the shared grid reads must not be a function of one board's filter.
	 * Deriving it from `visibleHires` meant one cell could report two different Missing values: narrow
	 * Hire to nights, apply a day hire, then open Move (whose filter is independent, and empty by
	 * default) and the day row was drawn understating its own coverage by an applied person who was still
	 * stored and still applied.
	 */
	const appliedGrid = useMemo(() => buildAppliedGrid(hires), [hires]);
	// `settings` is null until the payload has produced a selected job, and the summary band that reads
	// this mix is gated on the same thing — so there is no SHIFTS / WEEK to divide by yet, and nothing
	// renders the zeros.
	const mix = useMemo(() => (settings ? employmentMix(visibleHires, gridWeeks, settings.shiftsPerWeek) : EMPTY_MIX), [visibleHires, gridWeeks, settings]);

	// ---- Move ------------------------------------------------------------------------------------
	// One array on the wire carries every kind; this board filters to its own. `hire` is never surfaced
	// as a card (the Suggested Hires panel owns that gap) and `reduce` no longer has a board at all, so
	// the server still computes both and nothing here reads them.
	const recommendations = data?.recommendations ?? EMPTY_RECOMMENDATIONS;
	const { requested, addRequest, removeRequest, clearRequests } = useMoveRequests(moveRequestsStorageKey(userInfo.id, departmentId, effectiveJobId ?? ""));

	// Tone lookup for the still-short marker on a hire's day chips. Built from the SAME helpers the table
	// uses, over the SAME applied overlay, so a marker can never disagree with the cell above it.
	const { missingGrid, missingTones } = useMemo(() => {
		if (!data) {
			return { missingGrid: {} as MissingGrid, missingTones: {} as Record<string, (missing: number) => GapTone> };
		}
		const grids: HiringGridBundle = {
			neededGrid: data.neededGrid,
			actualGrid: data.actualGrid,
			appliedGrid,
			structuralOtGrid: data.structuralOtGrid,
			internalPickupOtGrid: data.internalPickupOtGrid,
			internalPickupNonOtGrid: data.internalPickupNonOtGrid,
			absencesGrid: data.absencesGrid,
		};
		const grid = buildMissingGrid(grids, includeFlags, gridWeeks);
		return { missingGrid: grid, missingTones: buildMissingToneFns(grid) };
	}, [data, appliedGrid, includeFlags, gridWeeks]);

	/**
	 * The Move cards worth showing. Two filters, and the second is not cosmetic.
	 *
	 * 1. The MOVE board's own shift filter — not the Hire tab's. Unfiltered, the board offered cards for
	 *    shift types the grid below wasn't drawing a row for (a move you can't see the gap for); with the
	 *    Hire tab's filter it hid moves for the shift you had narrowed your hiring away from.
	 * 2. The destination must STILL be short. Recommendations are computed server-side from a grid that
	 *    knows nothing about Apply, which is a client-side overlay — so applying two hires could close a
	 *    Sunday to `0` while the board went on proposing "move someone from Saturday into Sunday". The
	 *    card was inviting you to fill a gap you had just told the page was filled.
	 *
	 * Only the DESTINATION is re-checked. An applied hire only ever adds coverage, so it can close a
	 * deficit but never erase the surplus at the other end — the `fromSlot` stays valid by construction.
	 */
	const moveRecs = useMemo(
		() =>
			recommendationsOfType(recommendations, "move").filter((rec) => {
				if (!moveShiftsInScope.includes(rec.fromSlot?.shiftLabel ?? "")) {
					return false;
				}
				const to = rec.toSlot;
				if (!to) {
					return false;
				}
				const remaining = missingGrid[to.shiftLabel]?.[cellKey(to.weekIdx, to.dayIdx)];
				return remaining !== undefined && remaining > 0;
			}),
		[recommendations, moveShiftsInScope, missingGrid],
	);

	/**
	 * The remaining gap on ONE shift, on one day. The primitive both markers are built from — the row's
	 * (folded over that person's own shift types) and the Add-hire dialog's (one chosen shift) — so a dot
	 * cannot disagree with the cell under it whichever surface drew it.
	 */
	const gapToneFor = useCallback(
		(shiftLabel: string, key: number): GapTone => {
			// A label with no tone function has no ranked days (the shift isn't in the grid), and an absent
			// cell has no gap to report — both are honestly `neutral` rather than a guessed severity.
			const toneFn: ((missing: number) => GapTone) | undefined = missingTones[shiftLabel];
			const missing = missingGrid[shiftLabel]?.[key];
			return toneFn !== undefined && missing !== undefined ? toneFn(missing) : "neutral";
		},
		[missingGrid, missingTones],
	);

	/** Worst remaining gap on that day across THIS person's own shift types — never the whole grid's. */
	/**
	 * The days a newly ticked shift type brings with it: days that shift is still SHORT on, that this
	 * person is free for, up to their shifts-per-week cap.
	 *
	 * Widening used to change only the pill and the gap dots — the person covered a second shift on
	 * paper and worked none of it, so the edit read as inert. Removing a shift drops its days, so adding
	 * one has to bring days, or the control means two different things in its two directions.
	 *
	 * Resolved HERE rather than in the edit hook because the answer comes from `missingGrid` — the same
	 * numbers the cells are drawn from, so widening can never hand somebody a day the grid calls covered.
	 */
	const daysForNewShifts = (id: string, labels: string[]): Record<number, string> => {
		const hire = hires.find((candidate) => candidate.id === id);
		if (!hire || !settings) {
			return {};
		}
		const added = labels.filter((label) => !hire.coverShifts.includes(label));
		if (added.length === 0) {
			return {};
		}
		const perWeek = shiftsByWeek(hire, gridWeeks);
		const picked: Record<number, string> = {};
		for (const label of added) {
			for (let weekIdx = 0; weekIdx < gridWeeks; weekIdx += 1) {
				for (let dayIdx = 0; dayIdx < DAYS_PER_WEEK; dayIdx += 1) {
					const key = cellKey(weekIdx, dayIdx);
					// One shift per person per day, and never past the week they are allowed to work.
					if (hire.shiftByDay[key] !== undefined || picked[key] !== undefined || perWeek[weekIdx] >= settings.shiftsPerWeek) {
						continue;
					}
					if ((missingGrid[label]?.[key] ?? 0) > 0) {
						picked[key] = label;
						perWeek[weekIdx] += 1;
					}
				}
			}
		}
		return picked;
	};

	const markerToneFor = useCallback(
		(hire: SuggestedHireDraft, key: number): GapTone => {
			let worst: GapTone = "neutral";
			for (const label of hire.coverShifts) {
				const tone = gapToneFor(label, key);
				if (tone === "critical") {
					return "critical";
				}
				if (tone === "short") {
					worst = "short";
				}
			}
			return worst;
		},
		[gapToneFor],
	);

	/**
	 * The cells a hire's row points at: every day they work, on every shift type they cover.
	 *
	 * The cross product, not `shiftByDay`'s own label per day. A person covering three shift types was
	 * only lighting up the ONE row their days happened to be assigned to, so a row whose pill read
	 * "3PM-11PM + 11PM-7AM + 7AM-3PM" highlighted a single shift and looked broken — and it disagreed
	 * with the row's own day-chip dots, which already fold over every covered shift (`markerToneFor`).
	 *
	 * The two now say the same thing: hovering shows every gap this person is a candidate for, and the
	 * day assignment is what `buildAppliedGrid` uses when you actually Apply them.
	 */
	const hoveredHire = visibleHires.find((hire) => hire.id === hoveredHireId);
	const pinnedHire = visibleHires.find((hire) => hire.id === pinnedHireId);
	const hireHoverCoords = useMemo(() => (hoveredHire ? hireGridCoords(hoveredHire) : []), [hoveredHire]);
	const pinnedCoords = useMemo(() => (pinnedHire ? hireGridCoords(pinnedHire) : []), [pinnedHire]);

	/**
	 * The Hub is restricted to hiring roles, and the head nurses who live on this page usually have
	 * none — so an ungated link would 403 most users. When they can't reach it the segment is OMITTED
	 * rather than disabled: a permanently dead control that everyone can see is worse than one that
	 * isn't there.
	 */

	/**
	 * One shift chip toggled, on whichever board's filter the caller names. Written in terms of the
	 * SELECTED labels, where an empty list means "no filter" — the convention the store already had, and
	 * the reason this is not a one-line push/filter:
	 *
	 * • Switching the FIRST one off has to write out all the others explicitly, or an empty list would
	 *   read straight back as "everything on".
	 * • Switching the LAST one back on writes empty again rather than the full list, so a shift type
	 *   added to the master schedule later is in scope by default instead of silently filtered out.
	 * • The last remaining chip cannot be switched off: with no shift types there is nothing to suggest
	 *   and nothing to draw.
	 *
	 * Shared by both boards because the RULES are the same; the STORES are not, which is the whole point
	 * of taking the setter as an argument rather than closing over one.
	 */
	const toggleShiftIn = (setLabels: (update: (prev: string[]) => string[]) => void, board: HiringTab, label: string) => {
		setLabels((prev) => {
			const selected = activeShiftFilter(prev, allShiftLabels);
			const base = selected.size === 0 ? allShiftLabels : allShiftLabels.filter((existing) => selected.has(existing));
			const next = base.includes(label) ? base.filter((existing) => existing !== label) : [...base, label];
			if (next.length === 0) {
				return prev;
			}
			track("hiring_shift_filter_changed", { board, shift_toggled: label, shifts_in_scope: next.length });
			return next.length === allShiftLabels.length ? [] : next;
		});
	};

	const handleToggleHireShift = (label: string) => toggleShiftIn(setHireShiftFilterLabels, "hire", label);
	const handleToggleMoveShift = (label: string) => toggleShiftIn(setMoveShiftFilterLabels, "move", label);

	const handleToggleGridShift = (label: string) => {
		if (activeTab === "move") handleToggleMoveShift(label);
		else handleToggleHireShift(label);
	};

	const handleTabChange = (tab: HiringTab) => {
		setActiveTab(tab);
		// Leaving a board drops its transient hover highlight, which would otherwise stay painted on the
		// gaps table under the next board.
		setHoveredCoords([]);
		track("hiring_tab_changed", { tab, job_id: effectiveJobId });
	};

	const handleApplyToggle = (hire: SuggestedHireDraft) => {
		setApplied(hire.id, !hire.applied);
		track(hire.applied ? "hiring_suggested_hire_unapplied" : "hiring_suggested_hire_applied", {
			job_id: effectiveJobId,
			department_id: departmentId,
			shifts: hire.coverShifts.join(","),
			days: Object.keys(hire.shiftByDay).length,
		});
	};

	/**
	 * The dialog's Save & Apply. Everything the row needs is decided in there — the name, the one shift
	 * type, the days — so this only stores it and reports it.
	 */
	const handleCreateHire = (input: { name?: string; shiftLabel: string; shiftLength: number; shiftByDay: Record<number, string>; applied: boolean }) => {
		addHire(input);
		setAddHireOpen(false);
		track("hiring_suggested_hire_added", {
			job_id: effectiveJobId,
			department_id: departmentId,
			shift: input.shiftLabel,
			days: Object.keys(input.shiftByDay).length,
		});
	};

	/**
	 * A recurring shift was created or updated in the panel a suggested-hire row opened. That writes
	 * the department's schedule template — which is the Recurring row of the gaps grid, and the input
	 * every recommendation and suggestion is derived from — so the whole payload is refetched.
	 *
	 * Invalidated by KEY PREFIX, not by the exact key: the query is keyed by department, job, census
	 * and the metric levers, so a save is stale for every one of those variants, not just the one on
	 * screen. Never called on a plain cancel.
	 */
	/**
	 * A suggestion just became a REAL recurring shift, so the suggestion is spent.
	 *
	 * The row goes, rather than merely being un-applied. Two reasons, and the first is arithmetic: a
	 * draft left `applied` would keep overlaying a body the refetched `actualGrid` now carries itself,
	 * reporting coverage the department does not have. The second is what the row MEANS — it is a
	 * proposal to hire somebody, and somebody has now been put in the schedule from it. Leaving it on
	 * the list invites applying the same person a second time.
	 *
	 * Revert Changes still brings the whole generated set back if this was a mistake.
	 */
	const handleRecurringShiftSaved = (hireId: string) => {
		removeHire(hireId);
		void queryClient.invalidateQueries({ queryKey: [HIRING_QUERY_KEY] });
	};

	const handleReset = () => {
		resetHires();
		clearSettings();
		setPinnedHireId(null);
		track("hiring_suggested_hires_reset", { job_id: effectiveJobId, department_id: departmentId });
	};

	/** Un-marks one row. Deliberately inert beyond that — see the `Requested` chip in `MoveOpportunityCard`. */
	const handleUnrequestMove = (rec: HiringRecommendation, nurseId: number) => {
		removeRequest(recommendationKey(rec), nurseId);
		track("hiring_move_request_unmarked", { nurse_id: nurseId, job_id: rec.jobId });
	};

	const handleRevertRequests = () => {
		clearRequests();
		setRevertRequestsOpen(false);
		track("hiring_move_requests_reverted", { job_id: effectiveJobId, department_id: departmentId });
	};

	const handleJobChange = (jobId: string) => {
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				next.set(JOB_ID_QUERY_PARAM, jobId);
				return next;
			},
			{ replace: true },
		);
		// Written to BOTH: the URL keeps this visit shareable, the stored copy answers the next visit,
		// which arrives with no param at all.
		setStoredJobId(jobId);
		track("hiring_job_changed", { job_id: jobId });
	};

	const handleCensusConfirm = (next: number) => {
		setTargetCensus(next);
		setCensusModalOpen(false);
		track("hiring_census_changed", { census: next });
	};

	/** Nothing sends silently: this opens the broadcast composer with one locked recipient, prefilled. */
	/**
	 * Opens the composer for one person. The request is NOT recorded here — see `handleBroadcastClose`:
	 * this used to mark the row `Requested` the moment the composer opened, so discarding the message
	 * still left the board claiming the person had been asked.
	 *
	 * Re-entrant on purpose: clicking a row that already reads `Requested` opens the composer again, so
	 * following up with someone is one click rather than a Revert of the whole board.
	 */
	const handleRequestMove = (rec: HiringRecommendation, nurseId: number) => {
		if (!rec.fromSlot || !rec.toSlot) {
			return;
		}
		const message = t("hiring.broadcast.smsPrefill", {
			firstName: getNurseById(nurseId)?.firstName ?? "",
			// Day AND hours on both ends. A Move pairs two days on the SAME shift, so naming only the shift
			// made every message read "move from 11pm-7am to 11pm-7am".
			from: formatCoordForMessage(rec.fromSlot, t),
			to: formatCoordForMessage(rec.toSlot, t),
		});
		setBroadcastPrefill(message);
		setBroadcastNurseId(nurseId);
		setPendingRequest({ recKey: recommendationKey(rec), nurseId });
		track("hiring_request_move_clicked", { nurse_id: nurseId, job_id: rec.jobId });
	};

	/**
	 * The composer closed. `didSend` is true only when a message actually went out, which is the one
	 * thing that earns the row its `Requested` state — a discard leaves the board exactly as it was.
	 */
	const handleBroadcastClose = (didSend?: boolean) => {
		if (didSend && pendingRequest) {
			addRequest(pendingRequest.recKey, pendingRequest.nurseId);
			track("hiring_move_request_sent", { nurse_id: pendingRequest.nurseId, job_id: effectiveJobId });
		}
		setPendingRequest(null);
		setBroadcastNurseId(null);
	};

	const handleActualCellClick = (coord: HiringGridCoord) => {
		track("hiring_actual_cell_clicked", {
			week_idx: coord.weekIdx,
			day_idx: coord.dayIdx,
			shift_label: coord.shiftLabel,
			job_id: effectiveJobId,
		});
		setSelectedActualCoord(coord);
	};

	const pageShell = (children: React.ReactNode) => (
		<Box sx={{ bgcolor: SLATE[50], height: "100%", width: "100%", overflowY: "auto" }}>
			<Box sx={{ maxWidth: 1280, mx: "auto", px: 3, py: 4 }}>
				{/* The page's `h1`, VISUALLY HIDDEN. The visible title was deliberately removed — the mode
				    switch names where you are and every panel names itself — but nothing else supplies a
				    heading (not `Layout`, not `PageContainer`, and the nav's Hiring tab points at the Hub),
				    which left the page with no `h1` at all. `visuallyHidden` is MUI's own clip-based helper,
				    already used elsewhere in the app for exactly this. It lives in the shell so every page
				    state — loading, error, empty and the board itself — is announced with a name. */}
				<Box component="h1" sx={visuallyHidden} data-testid="hiring-page-heading">
					{t("hiring.page.title")}
				</Box>
				{children}
			</Box>
		</Box>
	);

	/**
	 * The page states that have no figure to show — one shape, so they read as siblings.
	 *
	 * The module name used to lead these, matching the page header; with that header gone the message
	 * IS the state, so it carries the weight instead of sitting muted under a title that repeated the
	 * nav. Deliberately not promoted to a heading element: these are sentences, not labels.
	 */
	const pageMessage = (testId: string, message: string) =>
		pageShell(
			<Box data-testid={testId}>
				<Typography sx={{ fontSize: FONT_SIZE.TITLE_SM, fontWeight: 600, color: INK.SECONDARY }}>{message}</Typography>
			</Box>,
		);

	if (gridWeeks === 0) {
		return pageMessage("hiring-page-not-available", t("hiring.page.notAvailable"));
	}
	// Staff rows and move requests are named and checked for SMS from the roster, so the page waits for it too.
	if (isLoading || isLoadingRoster) {
		return pageShell(<HiringPageSkeleton />);
	}
	if (isRosterError) {
		return pageShell(
			<Typography color="error" data-testid="hiring-page-roster-error">
				{t("profile.roster.unavailable")}
			</Typography>,
		);
	}
	if (isError || !data) {
		return pageShell(
			<Typography color="error" data-testid="hiring-page-error">
				{t("hiring.page.fetchError")}
			</Typography>,
		);
	}
	if (!data.censusDataAvailable) {
		return pageMessage("hiring-page-no-census", t("hiring.page.noCensusData"));
	}
	if (data.eligibleJobs.length === 0) {
		return pageMessage("hiring-page-empty", t("hiring.page.noEligibleJobs"));
	}
	if (!selectedJob || !settings) {
		// The selected job isn't in the payload's eligible set (a stale `job_id` in the URL). Nothing
		// downstream can be derived without it, so say so rather than rendering invented defaults.
		return pageMessage("hiring-page-job-unavailable", t("hiring.page.jobUnavailable"));
	}

	const effectiveCensus = targetCensus ?? data.targetCensus;

	/**
	 * The count each board answers with. Deliberately COMPACT — one bold phrase in a pill beside the
	 * tabs, not a display figure: the substance of every board is its rows, and a hero number would
	 * take weight away from them.
	 */
	const summaryByTab: Record<HiringTab, { count: number; label: string; chips: React.ReactNode }> = {
		hire: {
			count: visibleHires.length,
			label: t("hiring.hire.peopleToAdd"),
			chips: (
				<>
					<MetaChip
						label={t("hiring.hire.mixCount", { count: mix[EmploymentType.FullTime], type: t("employment.type.full.time") })}
						tooltip={t("hiring.hire.mixTarget", {
							actual: mixSharePercent(mix, EmploymentType.FullTime),
							target: Math.round(TARGET_EMPLOYMENT_MIX[EmploymentType.FullTime] * 100),
						})}
						tone="primary"
						sx={SUMMARY_CHIP_SX}
						dataTestId="hiring-hire-mix-full-time"
					/>
					<MetaChip
						label={t("hiring.hire.mixCount", { count: mix[EmploymentType.PartTime], type: t("employment.type.part.time") })}
						tooltip={t("hiring.hire.mixTarget", {
							actual: mixSharePercent(mix, EmploymentType.PartTime),
							target: Math.round(TARGET_EMPLOYMENT_MIX[EmploymentType.PartTime] * 100),
						})}
						tone="info"
						sx={SUMMARY_CHIP_SX}
						dataTestId="hiring-hire-mix-part-time"
					/>
					<MetaChip
						label={t("hiring.hire.mixCount", { count: mix[EmploymentType.PRN], type: t("employment.type.prn") })}
						tooltip={t("hiring.hire.mixTarget", {
							actual: mixSharePercent(mix, EmploymentType.PRN),
							target: Math.round(TARGET_EMPLOYMENT_MIX[EmploymentType.PRN] * 100),
						})}
						sx={SUMMARY_CHIP_SX}
						dataTestId="hiring-hire-mix-prn"
					/>
				</>
			),
		},
		move: {
			count: moveRecs.length,
			label: t("hiring.summary.moveLabel"),
			chips:
				requested.length > 0 ? (
					<MetaChip
						label={t("hiring.summary.requestedCount", { count: requested.length })}
						tone="success"
						sx={SUMMARY_CHIP_SX}
						dataTestId="hiring-summary-requested"
					/>
				) : null,
		},
	};
	const summary = summaryByTab[activeTab];

	return pageShell(
		<Box data-testid="hiring-prediction-page">
			{/* No page title: the job pill and the panels below name themselves, so a heading would be a
			    third label for the same thing. The header block is gone rather than left empty — the
			    control row is the top of the page and carries the shell's own `py` as its breathing room.
			    A Prediction/Hub mode switch used to lead this row; it is gone with the second Hub entry
			    point it existed for, which leaves one segment that could never change anything. */}
			<Box sx={{ mb: 2, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
				{/* ONE pill with a dropdown, data-driven from the payload: departments run anywhere from one
				    eligible job to many, and a pill per job stops fitting this row long before the list does. */}
				<SystemDropdown
					items={jobDropdownItems}
					selectedItem={selectedJob.jobId}
					onChange={(value) => value && handleJobChange(value)}
					trigger="pill"
					formControlSx={{ m: 0 }}
					selectSx={jobPillSx}
					data-testid="hiring-job-picker"
				/>
				{/* Bare icon + text, no chip: the census is context, not a control competing with the mode
				    switch and the job pills beside it. `ml: auto` is the only addition to the original —
				    it right-aligns it on this shared row (it used to own a row of its own). This is the one
				    place `styleTokens`' SLATE/BLUE stay in use, deliberately. */}
				<ButtonBase
					data-testid="hiring-census-pill"
					onClick={() => setCensusModalOpen(true)}
					sx={{
						ml: "auto",
						display: "inline-flex",
						alignItems: "center",
						gap: 0.75,
						fontSize: 14,
						fontWeight: 700,
						color: SLATE[400],
						transition: "color 150ms ease",
						"&:hover": { color: BLUE[600] },
					}}
				>
					<GroupOutlinedIcon sx={{ fontSize: 16 }} />
					{t("hiring.page.targetCensus", { census: effectiveCensus })}
				</ButtonBase>
			</Box>

			<Box sx={{ mb: 2.5, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
				<SystemFlatSwitch<HiringTab>
					equalWidthOptions
					delayCallback={0}
					shape="rectangular"
					rootWidth={200}
					// The design's blue, fixed rather than brand-derived (see `ACCENT_BLUE`).
					activeColor={ACCENT_BLUE.INK}
					options={[
						{ value: "hire", label: t("hiring.tab.hire"), "data-testid": "hiring-tab-hire" },
						{ value: "move", label: t("hiring.tab.move"), "data-testid": "hiring-tab-move" },
					]}
					activeIndexOrValue={activeTab}
					onSwitch={(option) => handleTabChange(option.value)}
					data-testid="hiring-tabs"
				/>
				<StatStrip
					layout="row"
					surface="paper"
					labelCase="sentence"
					dataTestId="hiring-tab-summary"
					items={[{ label: summary.label, value: <AnimatedNumber value={summary.count} />, dataTestId: "hiring-tab-summary-count" }]}
					endSlot={summary.chips ? <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>{summary.chips}</Box> : undefined}
				/>
			</Box>

			{activeTab === "hire" && (
				<SuggestedHiresPanel
					hires={visibleHires}
					settings={settings}
					gridWeeks={gridWeeks}
					weekNumbers={data.weekNumbers}
					weekStartDay={weekStartDay}
					dates={data.dates}
					shiftLabels={allShiftLabels}
					enabledShiftLabels={hireShiftsInScope}
					shiftLengthOptions={shiftLengthOptions}
					jobId={selectedJob.jobId}
					hasEdits={hasEdits || storedSettings !== null}
					markerToneFor={markerToneFor}
					selectedHireId={pinnedHireId}
					onSelectHire={setPinnedHireId}
					onHoverHire={setHoveredHireId}
					onRename={renameHire}
					// `allShiftLabels`, NOT `shiftLabels`: the picker offers every label the grid can draw a row
					// for, and `shiftLabels` is only the needed-grid subset. A shift with coverage but no
					// Needed row is pickable and absent from that subset, so `indexOf` returned -1 and sorted
					// it to the FRONT — taking the hours pill and the recurring handoff with it.
					onShiftsChange={(id, labels) => setHireShifts(id, labels, allShiftLabels, daysForNewShifts(id, labels))}
					onDayChange={setDay}
					onApplyToggle={handleApplyToggle}
					onRemove={removeHire}
					onAddHire={() => setAddHireOpen(true)}
					onReset={handleReset}
					onRecurringShiftSaved={handleRecurringShiftSaved}
					onToggleShift={handleToggleHireShift}
					onGenerationChange={(next) => {
						// Stored rows are NOT dropped. Every field here is a query input, so the server does
						// regenerate — but once the user has edits that stored list stays authoritative
						// (`useSuggestedHireEdits`), and only Revert Changes hands the page back to the
						// server's set. SHIFTS / WEEK is the badge denominator, so writing the setting is by
						// itself enough to re-derive every badge on the existing rows with no round trip.
						//
						// SHIFT TYPES PER PERSON is the one that can look inert: it changes only what the server
						// GENERATES, so a user who already has stored rows sees the same list until they
						// Revert. That is the module's standing promise that a setting never discards work.
						const merged = { ...settings, ...next };
						setStoredSettings(merged);
						// The MERGED settings, not the change: the payload answers "what are they generating
						// against now", and a partial change would report the untouched fields as undefined.
						track("hiring_hire_settings_changed", {
							shift_length: merged.shiftLength,
							shifts_per_week: merged.shiftsPerWeek,
							shift_types: merged.shiftTypes,
						});
					}}
				/>
			)}

			{activeTab === "move" && (
				<MoveOpportunitiesPanel
					recommendations={moveRecs}
					weekNumbers={data.weekNumbers}
					dates={data.dates}
					weekStartDay={weekStartDay}
					jobDisplayName={selectedJobDisplayName}
					shiftLabels={allShiftLabels}
					enabledShiftLabels={moveShiftsInScope}
					onToggleShift={handleToggleMoveShift}
					requested={requested}
					canRevert={requested.length > 0}
					onRevert={() => setRevertRequestsOpen(true)}
					onHoverChange={setHoveredCoords}
					onRequestMove={handleRequestMove}
					onUnrequestMove={handleUnrequestMove}
					getNurseById={getNurseById}
				/>
			)}

			<Box>
				<RecurringGapsTable
					dates={data.dates}
					timezone={timezone}
					neededGrid={data.neededGrid}
					actualGrid={data.actualGrid}
					structuralOtGrid={data.structuralOtGrid}
					internalPickupOtGrid={data.internalPickupOtGrid}
					internalPickupNonOtGrid={data.internalPickupNonOtGrid}
					agencyGrid={data.agencyGrid}
					absencesGrid={data.absencesGrid}
					appliedGrid={appliedGrid}
					templateHppd={data.templateHppd}
					highlightedCoords={activeTab === "hire" ? hireHoverCoords : hoveredCoords}
					pinnedCoords={pinnedCoords}
					weekStartDay={weekStartDay}
					gridWeeks={gridWeeks}
					weekNumbers={data.weekNumbers}
					onActualCellClick={handleActualCellClick}
					includeFlags={includeFlags}
					onToggleMetric={toggleMetric}
					shiftFilterLabels={gridShiftFilterLabels}
					onToggleShift={handleToggleGridShift}
					// Whose view preferences these are. Passed as one scope rather than three loose ids so the
					// table can build its own keys from the shared builders without reading a context — it
					// stays a presentational component that is told who it is rendering for.
					storageScope={{ userId: userInfo.id, departmentId, jobId: selectedJob.jobId }}
					jobDisplayName={selectedJobDisplayName}
				/>
			</Box>

			{/* Only rendered with real template labels behind it: the dialog picks one shift type, and there
			    is nothing to pick — or to save — without them. `shiftLabels` is the job's WHOLE template
			    set, NOT the Hire filter's subset: that filter narrows who gets suggested, and must not
			    narrow what you can create by hand. */}
			{shiftLabels.length > 0 && (
				<AddHireDialog
					open={addHireOpen}
					onClose={() => setAddHireOpen(false)}
					shiftLabels={shiftLabels}
					templateHours={templateHours}
					gridWeeks={gridWeeks}
					weekNumbers={data.weekNumbers}
					weekStartDay={weekStartDay}
					dates={data.dates}
					timezone={timezone}
					jobOptions={jobDropdownItems.map((item) => ({ jobId: item.value, displayName: item.label }))}
					jobId={selectedJob.jobId}
					jobDisplayName={selectedJobDisplayName}
					// The SAME handler the header job pill uses: a hire only exists inside a job, so picking a
					// role in the dialog moves the whole page rather than filing the person under the wrong one.
					onJobChange={handleJobChange}
					gapToneFor={gapToneFor}
					onCreate={handleCreateHire}
				/>
			)}

			<Dialog
				open={revertRequestsOpen}
				onClose={() => setRevertRequestsOpen(false)}
				title={t("hiring.move.revertConfirm.title")}
				body={t("hiring.move.revertConfirm.body", { count: requested.length })}
				confirm={t("hiring.move.revertConfirm.confirm")}
				confirmButtonVariant="delete"
				onConfirm={handleRevertRequests}
				dataTestId="hiring-move-revert-confirm"
			/>

			<CensusModal
				open={censusModalOpen}
				onClose={() => setCensusModalOpen(false)}
				onConfirm={handleCensusConfirm}
				currentCensus={effectiveCensus}
				baselineCensus={data.targetCensus}
				censusDistribution={data.censusDistribution ?? []}
			/>
			{selectedActualCoord && (
				<ActualCellNursesModal
					open={Boolean(selectedActualCoord)}
					onClose={() => setSelectedActualCoord(null)}
					coord={selectedActualCoord}
					jobId={selectedJob.jobId}
					jobDisplayName={selectedJobDisplayName}
				/>
			)}
			{broadcastNurseId !== null && (
				<NewBroadcastMessageModal
					onClose={handleBroadcastClose}
					initialSmsMessage={broadcastPrefill}
					initialSelectedNurseIds={[broadcastNurseId]}
					lockRecipientSelection={true}
				/>
			)}
		</Box>,
	);
};

export default HiringModule;
